import 'devextreme/dist/css/dx.light.css';
import { Route, Routes } from 'react-router-dom';
import { Suspense, lazy, useContext, useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { getTheme } from './theme'; // Importar la función para obtener el tema dinámico
import { UserContext, AnnouncementProvider } from './hooks';
import { BaseLayout } from './components';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Schedule } from './pages';

// Lazy loading de las páginas
const Admin = lazy(() => import('./pages/AdminPanel/AdminPanel'));
const Reset = lazy(() => import('./pages/Reset'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ResendVerification = lazy(() => import('./pages/ResendVerification'));
const Register = lazy(() => import('./pages/Register'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const Courts = lazy(() => import('./pages/HomePage/Courts'));
const Access = lazy(() => import('./pages/AdminPanel/Access'));
const BlockedTimes = lazy(
  () => import('./pages/AdminPanel/BlockedTimes/BlockedTimesPage')
);
const AdminUsers = lazy(() => import('./pages/AdminPanel/AdminUsersPage'));
const AdminReservations = lazy(
  () => import('./pages/AdminPanel/AdminReservationsPage')
);
const AdminCourts = lazy(() => import('./pages/AdminPanel/AdminCourtsPage'));
const AdminAccess = lazy(() => import('./pages/AdminPanel/AdminAccessPage'));
const AdminStatistics = lazy(
  () => import('./pages/AdminPanel/AdminStatisticsPage')
);
const AdminSettings = lazy(
  () => import('./pages/AdminPanel/AdminSettingsPage')
);
const Profile = lazy(() => import('./pages/HomePage/Profile/Profile'));
const Bookings = lazy(() => import('./pages/HomePage/Bookings/Bookings'));
const BookingDetail = lazy(
  () => import('./pages/HomePage/BookingDetail/BookingDetail')
);
const Membership = lazy(() => import('./pages/HomePage/Membership/Membership'));
const TennisSchool = lazy(
  () => import('./pages/HomePage/TennisSchool/TennisSchool')
);
const Shop = lazy(() => import('./pages/HomePage/Shop/Shop'));
const LogIn = lazy(() => import('./pages/LogIn'));
const UnlockDoor = lazy(() => import('./pages/UnlockDoor'));

function App() {
  const { isMember } = useContext(UserContext); // 🔥 Obtiene isMember del contexto
  const [theme, setTheme] = useState(getTheme(isMember)); // 🔥 Inicializa el tema con isMember

  // 🔥 Actualiza el tema cuando isMember cambia
  useEffect(() => {
    setTheme(getTheme(isMember));
  }, [isMember]);

  return (
    <AnnouncementProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <Routes>
            <Route exact path="/" element={<LogIn />} />
            <Route exact path="/unlock" element={<UnlockDoor />} />
            <Route exact path="/reset" element={<Reset />} />
            <Route exact path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              path="/resend-verification"
              element={<ResendVerification />}
            />
            <Route path="/success" element={<PaymentSuccess />} />
            <Route path="/cancel" element={<PaymentCancel />} />

            {/* Rutas protegidas con el layout */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <BaseLayout>
                    <Courts />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BaseLayout>
                    <Admin />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BaseLayout>
                    <AdminUsers />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reservations"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BaseLayout>
                    <AdminReservations />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/courts"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BaseLayout>
                    <AdminCourts />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/access"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BaseLayout>
                    <AdminAccess />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/statistics"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BaseLayout>
                    <AdminStatistics />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blocked-times"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BaseLayout>
                    <BlockedTimes />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BaseLayout>
                    <AdminSettings />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <BaseLayout>
                    <Profile />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="/membership"
              element={
                <ProtectedRoute>
                  <BaseLayout>
                    <Membership />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <ProtectedRoute>
                  <BaseLayout>
                    <Bookings />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/:bookingId"
              element={
                <ProtectedRoute>
                  <BaseLayout>
                    <BookingDetail />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/courts/:courtId/schedule"
              element={
                <ProtectedRoute>
                  <BaseLayout>
                    <Schedule />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tennis-school"
              element={
                <ProtectedRoute>
                  <BaseLayout>
                    <TennisSchool />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop"
              element={
                <ProtectedRoute>
                  <BaseLayout>
                    <Shop />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/access"
              element={
                <ProtectedRoute>
                  <BaseLayout>
                    <Access />
                  </BaseLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </ThemeProvider>
    </AnnouncementProvider>
  );
}

export default App;
